import { FC, useContext, useEffect, useRef, useState } from 'react';
import SingleProposalBody from './SingleProposalBody';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import { UpdateProposalParams } from 'ts/shared/interfaces/CreateProposalParams';
import EditProposalModal from './EditProposalModal';
import { EditProposalContext } from 'contexts/EditProposaContext';
import { getReportList } from 'utils/shared/proposal';
import { updateProposalD } from 'services/cms';
import { formatPriceProposal } from 'utils/shared/utils';
import EditPriceModal from '../Create/EditPriceModal';
import md5 from 'md5';
import { buildProposalUpdateInputData } from 'utils/shared/tripBuilder';
import { resolveLanguageFromProposal } from 'utils/shared/tripResultBuilderPreview';
import { MainContext } from 'contexts/MainContext';
import { useProposalUi } from '../Assistant/proposalActions';

interface ProposalEditProps {
  editDate?: Date;
  onUpdatingChange?: (isUpdating: boolean) => void;
  template?: string;
  sort?: string;
  language?: string;
  onProposalSaved?: (proposal: ProposalData) => void;
  assistantOwner?: boolean;
}

const ProposalEdit: FC<ProposalEditProps> = ({
  editDate,
  onUpdatingChange,
  template,
  sort,
  language,
  onProposalSaved,
  assistantOwner = false,
}) => {
  const { proposal, setProposal } = useContext(MainContext);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editIndex, setEditIndex] = useState(0);
  const [showEditPriceModal, setShowEditPriceModal] = useState(false);
  const initLoading = useRef(true);

  useEffect(() => {
    if (editDate && !initLoading.current) {
      updateProposal();
    }
    initLoading.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editDate]);

  const updateProposal = async () => {
    const { existingProposal } = proposal;

    if (!existingProposal) {
      onUpdatingChange?.(false);
      return;
    }

    onUpdatingChange?.(true);

    try {
      const productsToDelete = existingProposal.listOfProducts
        .filter((product) => product.removed)
        .map((product) => {
          if (product.listOfItems.length > 1) {
            return {
              reportName: 2,
              dynamicProduct: {
                id: product.id,
                amount: product.amount,
                currency: product.currency,
                salePrice: product.salePrice,
                note: product.note,
                displayPrice: product.displayPrice,
                itemsKeysForDelete: [product.id],
              },
            };
          } else if (product.listOfItems[0].proposal.code === 'HTL') {
            return {
              reportName: 0,
              hotelProduct: {
                id: product.id,
                amount: product.amount,
                currency: product.currency,
                salePrice: product.salePrice,
                note: product.note,
                displayPrice: product.displayPrice,
                itemsKeysForDelete: [product.id],
              },
            };
          } else if (product.listOfItems[0].proposal.code === 'FLT') {
            return {
              reportName: 1,
              flightProduct: {
                id: product.id,
                amount: product.amount,
                currency: product.currency,
                salePrice: product.salePrice,
                note: product.note,
                displayPrice: product.displayPrice,
                itemsKeysForDelete: [product.id],
              },
            };
          } else if (product.listOfItems[0].proposal.code === 'TRS') {
            return {
              reportName: 5,
              transferProduct: {
                id: product.id,
                amount: product.amount,
                currency: product.currency,
                salePrice: product.salePrice,
                note: product.note,
                displayPrice: product.displayPrice,
                itemsKeysForDelete: [product.id],
              },
            };
          } else {
            return {
              reportName: 8,
              ilPackageProduct: {
                id: product.id,
                amount: product.amount,
                currency: product.currency,
                salePrice: product.salePrice,
                note: product.note,
                displayPrice: product.displayPrice,
                itemsKeysForDelete: [product.id],
              },
            };
          }
        });

      const productsToEdit = existingProposal.listOfProducts
        .filter((product) => !product.removed && product.edited)
        .map((product) => {
          const salePrice =
            typeof product.viewValue === 'number'
              ? parseInt(
                formatPriceProposal(
                  product.amount,
                  product.currency,
                  product.viewType,
                  product.viewValue
                ).replace(/[^\d]/g, ''),
                10
              )
              : product.salePrice;
          if (product.listOfItems.length > 1) {
            return {
              reportName: 2,
              dynamicProduct: {
                id: product.id,
                amount: product.amount,
                currency: product.currency,
                salePrice: salePrice,
                note: product.note,
                displayPrice: product.displayPrice,
                listOfItemsKeysForUpdate: product.listOfItems,
              },
            };
          } else if (product.listOfItems[0].proposal.code === 'HTL') {
            return {
              reportName: 0,
              hotelProduct: {
                id: product.id,
                amount: product.amount,
                currency: product.currency,
                salePrice: salePrice,
                note: product.note,
                displayPrice: product.displayPrice,
                listOfItemsKeysForUpdate: product.listOfItems,
                showCancellationDate: product.showCancellationDate,
                cancellationDateDelta: product.cancellationDateDelta,
              },
            };
          } else if (product.listOfItems[0].proposal.code === 'FLT') {
            return {
              reportName: 1,
              flightProduct: {
                id: product.id,
                amount: product.amount,
                currency: product.currency,
                salePrice: salePrice,
                note: product.note,
                displayPrice: product.displayPrice,
                listOfItemsKeysForUpdate: product.listOfItems,
              },
            };
          } else if (product.listOfItems[0].proposal.code === 'TRS') {
            return {
              reportName: 5,
              transferProduct: {
                id: product.id,
                amount: product.amount,
                currency: product.currency,
                salePrice: salePrice,
                note: product.note,
                displayPrice: product.displayPrice,
                listOfItemsKeysForUpdate: product.listOfItems,
              },
            };
          } else {
            return {
              reportName: 8,
              ilPackageProduct: {
                id: product.id,
                amount: product.amount,
                currency: product.currency,
                salePrice: salePrice,
                note: product.note,
                displayPrice: product.displayPrice,
                listOfItemsKeysForUpdate: product.listOfItems,
              },
            };
          }
        });

      const nextLanguage = language || resolveLanguageFromProposal(existingProposal.externalParam_1);
      const productsToAdd = getReportList(proposal, { language: nextLanguage });

      const key = existingProposal.id + '_' + md5(existingProposal.id + '') + '_' + existingProposal.userC + '_' + existingProposal.companyId;
      const inputData = buildProposalUpdateInputData(existingProposal, {
        template,
        sort,
        language: nextLanguage,
      });
      const params: UpdateProposalParams = {
        previewKeyPublic: key,
        reportsList: [...productsToDelete, ...productsToEdit, ...productsToAdd],
        proposalId: existingProposal.id,
        comments: existingProposal.note,
        ...(inputData ? { inputData } : {}),
      };

      const response = await updateProposalD(params);

      if (response?.updated) {
        const savedProposal = response.proposal || existingProposal;
        setProposal({
          hotels: [],
          flights: [],
          dynamic: [],
          ilPackages: [],
          transfers: [],
          existingProposal: savedProposal,
        });
        onProposalSaved?.(savedProposal);
      }
    } finally {
      onUpdatingChange?.(false);
    }
  };

  useProposalUi(
    (intent) =>
      !!assistantOwner &&
      (intent.kind === 'open-price' || intent.kind === 'open-edit') &&
      !!proposal.existingProposal,
    (intent) => {
      if (intent.kind === 'open-price') {
        const products = proposal.existingProposal?.listOfProducts || [];
        const index =
          typeof intent.index === 'number' && intent.index >= 0 && intent.index < products.length
            ? intent.index
            : 0;
        setEditIndex(index);
        setShowEditPriceModal(true);
      }
      if (intent.kind === 'open-edit') {
        const products = proposal.existingProposal?.listOfProducts || [];
        const index =
          typeof intent.index === 'number' && intent.index >= 0 && intent.index < products.length
            ? intent.index
            : 0;
        setEditIndex(index);
        setShowEditModal(true);
      }
    },
  );

  const onClickEdit = (index: number) => {
    setShowEditModal(true);
    setEditIndex(index);
  };

  const onUpdateModalSave = (newProposal: any) => {
    const newProposalData = { ...proposal.existingProposal };
    if (newProposalData.listOfProducts) {
      newProposalData.listOfProducts[editIndex] = newProposal;
      setProposal({ ...proposal, existingProposal: newProposalData as ProposalData });
    }
  };

  const onClickDelete = (index: number) => {
    let newProposal = { ...proposal.existingProposal };

    if (newProposal.listOfProducts?.[index]) {
      newProposal.listOfProducts[index].removed = !newProposal.listOfProducts[index].removed;
      setProposal({ ...proposal, existingProposal: newProposal as ProposalData });
    }
  };

  const onClickEditPrice = (index: number) => {
    setShowEditPriceModal(true);
    setEditIndex(index);
  };

  const onCloseEditPrice = (type?: 'p' | 'v' | 'e', value?: number, isAll?: boolean) => {
    setShowEditPriceModal(false);
    setEditIndex(0);

    if (isAll && type && typeof value === 'number') {
      let newProposal = { ...proposal };
      let listOfProducts = proposal.existingProposal?.listOfProducts;

      listOfProducts?.forEach((product) => {
        product.viewType = type;
        product.viewValue = value;
        product.edited = true;

        if (product.listOfItems[0]?.hotels?.[0]?.rooms?.length) {
          const rooms = product.listOfItems[0]?.hotels?.[0]?.rooms;
          const newRooms = rooms.map((r) => {
            return {
              ...r,
              supplements: [
                {
                  ...r.supplements?.[0],
                  code: formatPriceProposal(
                    +r.supplements?.[0]?.type,
                    r.supplements[0].name,
                    type,
                    value
                  ).replace(/[^\d]/g, ''),
                },
              ],
            };
          });
          product.listOfItems[0].hotels[0].rooms = newRooms;
        }
      });

      newProposal.dynamic = newProposal.dynamic.map((d) => {
        return {
          ...d,
          viewType: type,
          viewValue: value,
        };
      });
      newProposal.flights = newProposal.flights.map((f) => {
        return {
          ...f,
          viewType: type,
          viewValue: value,
        };
      });
      newProposal.transfers = newProposal.transfers.map((t) => {
        return {
          ...t,
          viewType: type,
          viewValue: value,
        };
      });
      newProposal.ilPackages = newProposal.ilPackages.map((p) => {
        return {
          ...p,
          viewType: type,
          viewValue: value,
        };
      });
      newProposal.hotels = newProposal.hotels.map((h) => {
        return {
          ...h,
          viewType: type,
          viewValue: value,
        };
      });
      setProposal({
        ...newProposal,
        existingProposal: { ...newProposal.existingProposal, listOfProducts } as any,
      });
    } else if (type && typeof value === 'number') {
      let newProposal = { ...proposal.existingProposal };
      if (newProposal.listOfProducts?.[editIndex]) {
        newProposal.listOfProducts[editIndex].viewType = type;
        newProposal.listOfProducts[editIndex].viewValue = value;
        newProposal.listOfProducts[editIndex].edited = true;

        if (newProposal.listOfProducts[editIndex].listOfItems[0]?.hotels?.[0]?.rooms?.length) {
          const rooms = newProposal.listOfProducts[editIndex].listOfItems[0]?.hotels?.[0]?.rooms;
          const newRooms = rooms.map((r) => {
            return {
              ...r,
              supplements: [
                {
                  ...r.supplements?.[0],
                  code: formatPriceProposal(
                    +r.supplements?.[0]?.type,
                    r.supplements[0].name,
                    type,
                    value
                  ).replace(/[^\d]/g, ''),
                },
              ],
            };
          });
          newProposal.listOfProducts[editIndex].listOfItems[0].hotels[0].rooms = newRooms;
        }
        setProposal({ ...proposal, existingProposal: newProposal as ProposalData });
      }
    }
  };

  return (
    <EditProposalContext.Provider
      value={{
        editMode: true,
        onClickEdit,
        onClickDelete,
        onClickEditPrice,
        proposal: proposal.existingProposal,
      }}
    >
      {!!proposal.existingProposal && (
        <SingleProposalBody proposal={proposal.existingProposal} template={'t1'} sort={'1'} />
      )}
      {showEditModal && (
        <EditProposalModal
          close={() => setShowEditModal(false)}
          onSave={onUpdateModalSave}
          proposalData={proposal.existingProposal?.listOfProducts?.[editIndex]}
        />
      )}
      {showEditPriceModal && (
        <EditPriceModal
          isOpen={showEditPriceModal}
          initialProduct={{
            product: { price: proposal.existingProposal?.listOfProducts?.[editIndex]?.salePrice },
          }}
          openData={{
            value:
              proposal.existingProposal?.listOfProducts?.[editIndex]?.viewValue?.toString() || '',
            type:
              (proposal.existingProposal?.listOfProducts?.[editIndex]?.viewType as
                | 'p'
                | 'v'
                | 'e') || 'v',
          }}
          onClose={onCloseEditPrice}
        />
      )}
    </EditProposalContext.Provider>
  );
};

export default ProposalEdit;
